// DistrictPicker — the ONE district control. Search box on top, city-scoped tree
// underneath, single- or multi-select.
// ============================================================================
// Owner, 2026-08-08: "make one canonical form … implement that structure
// everywhere so everything writes and reads similarly."
//
// It replaces five separate implementations, two of which lived in the same file:
//
//   src/components/IntakeTab.jsx      search sidebar: tree + counts + gray-out
//   src/components/desk/ManualPropertyModal.jsx  <select> + optgroups, city never passed
//   src/components/desk/ManualClientModal.jsx    flat list grouped by d.city
//   src/fill/FillApp.jsx  (where step)           search + tree + city chips
//   src/fill/FillApp.jsx  (p_addr step)          raw flat <select>, 236 rows
//
// The last one is why a Tbilisi address could show Batumi's "აეროპორტი": with no
// city scope and Georgian alphabetical order, the Adjara/Batumi block sorts first
// and Tbilisi is ~50 rows down a control with no search.
//
// Shape is taken from the FILL FORM's where step, not the desk sidebar: that one
// is the version real clients have used on real phones, and the sidebar's counts
// and gray-out are search affordances that would be actively wrong here — on a
// capture form, adding the first property in an empty district is the whole point.
//
// ⚠️ RENDERED IN BOTH BUNDLES, so it obeys the fill form's contract: React and
// lib/districtModel are the ONLY imports. No i18n, no db, no design-system
// primitives — every string arrives as a prop, because the public bundle carries
// its own self-contained STR table and must not pull the app's i18n machinery in.
// ============================================================================

import { useMemo, useState } from 'react'
import { buildDistrictTree, citiesOf, districtLabel, districtPath, searchDistricts } from '../lib/districtModel.js'

const BOX = 'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground'

// A row is a button, never a bare div: this is reached by keyboard and by screen
// reader on the public form, and "tap the text" is not an affordance a div has.
function Row({ label, selected, indent, onPick, badge }) {
  return (
    <button type="button" onClick={onPick} aria-pressed={selected}
      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm
        ${selected ? 'bg-primary/10 font-medium text-foreground' : 'text-foreground hover:bg-accent'}`}
      style={{ paddingLeft: 8 + indent * 14 }}>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge}
      {selected && <span aria-hidden className="shrink-0 text-xs">✓</span>}
    </button>
  )
}

export default function DistrictPicker({
  districts = [],
  lang = 'ka',
  mode = 'single',                 // 'single' → code | null ; 'multi' → code[]
  value = null,
  onChange,
  city = null,                     // null = the whole country
  onCityChange = null,             // omit to hide the city row (caller pins the scope)
  labels = {},
  maxHeight = 260,
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(() => new Set())

  const L = {
    search: 'Search districts…', allCities: 'All of Georgia', empty: 'Nothing matches',
    hint: 'Tap a district for the whole area, or › to narrow to a neighbourhood.',
    flat: 'Neighbourhood hierarchy unavailable — showing a flat list.',
    // Two controls, two questions. They were rendered as one block under a single
    // "District" heading, which said that Tbilisi is a district (owner, 2026-08-08:
    // "tbilisi is city not უბანი"). A city is not a district and the selector that
    // picks one has to say so.
    city: 'City', district: 'District', peri: 'Around Tbilisi',
    ...labels,
  }
  const selected = useMemo(
    () => new Set(mode === 'multi' ? (value || []) : value ? [value] : []),
    [mode, value])

  const { roots, childrenOf, byCode, hierarchical } = useMemo(
    () => buildDistrictTree(districts, city, { lang }), [districts, city, lang])
  const cities = useMemo(() => citiesOf(districts, lang), [districts, lang])
  const hits = useMemo(
    () => searchDistricts(districts, q, { city, lang }), [districts, q, city, lang])
  // The city's own districts vs the belt around it. Only Tbilisi has one, so every
  // other scope keeps a single ungrouped list — an empty section header would be
  // noise, and a header over the ONLY group is worse than none.
  const inTown = useMemo(() => roots.filter(d => !/^PERI_/.test(d.code)), [roots])
  const around = useMemo(() => roots.filter(d => /^PERI_/.test(d.code)), [roots])

  const pick = code => {
    if (mode === 'multi') {
      const cur = value || []
      onChange(cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code])
    } else {
      // Tapping the live choice clears it. A single-select with no way back forces
      // a wrong answer to be replaced by another wrong answer.
      onChange(value === code ? null : code)
    }
  }
  const toggleOpen = code => setOpen(prev => {
    const next = new Set(prev)
    if (next.has(code)) next.delete(code); else next.add(code)
    return next
  })

  // A raion opens when it holds a selection, so a restored value can never hide
  // itself inside a collapsed parent.
  const isOpen = d => open.has(d.code) || (childrenOf.get(d.code) || []).some(k => selected.has(k.code))

  const renderNode = (d, depth) => {
    const kids = childrenOf.get(d.code) || []
    const openNow = kids.length > 0 && isOpen(d)
    return (
      <div key={d.code}>
        <div className="flex items-center gap-1">
          {/* The disclosure is its own control, never the row: opening a raion to
              look inside is not the same act as choosing the whole raion, and one
              tap doing both is how you get a district nobody picked. */}
          {kids.length > 0 ? (
            <button type="button" aria-expanded={openNow} aria-label={districtLabel(d, lang)}
              className="w-4 shrink-0 text-[10px] text-muted-foreground hover:text-foreground"
              onClick={() => toggleOpen(d.code)}>{openNow ? '▾' : '▸'}</button>
          ) : <span className="w-4 shrink-0" />}
          <div className="min-w-0 flex-1">
            <Row label={districtLabel(d, lang)} selected={selected.has(d.code)}
              indent={depth} onPick={() => pick(d.code)} />
          </div>
        </div>
        {openNow && (
          <div className="ml-2 border-l border-border/50">
            {kids.map(k => renderNode(k, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {onCityChange && (
        <label className="block text-xs text-muted-foreground">{L.city}
          <select className={BOX} value={city || ''} onChange={e => onCityChange(e.target.value || null)}>
            <option value="">{L.allCities}</option>
            {cities.map(c => <option key={c.code} value={c.city || c.name}>{c.name}</option>)}
          </select>
        </label>
      )}

      <label className="block text-xs text-muted-foreground">{L.district}
        <input className={BOX} value={q} placeholder={L.search} autoComplete="off" spellCheck={false}
          onChange={e => setQ(e.target.value)} />
      </label>

      {/* Selected-but-out-of-view is the trap a search box adds: filter the tree
          and the current answer vanishes, which reads as "it forgot". The chips
          are always present, so the answer is visible whatever the query says. */}
      {selected.size > 0 && (
        <div className="flex flex-wrap gap-1">
          {[...selected].map(code => {
            const d = byCode.get(code) || districts.find(x => x.code === code)
            return (
              <button key={code} type="button" onClick={() => pick(code)}
                className="rounded-full border border-border bg-accent px-2 py-0.5 text-xs text-foreground">
                {d ? districtLabel(d, lang) : code} ✕
              </button>
            )
          })}
        </div>
      )}

      <div className="overflow-y-auto rounded-xl border border-border" style={{ maxHeight }}>
        {q.trim() ? (
          hits.length === 0
            ? <p className="px-3 py-3 text-sm text-muted-foreground">{L.empty}</p>
            // Hits are FLAT and carry their ancestry: a bare "Rustaveli" is
            // ambiguous in a country with several, and the path is what makes a
            // result safe to pick without opening the tree to check.
            : hits.map(d => (
              <Row key={d.code} indent={0} selected={selected.has(d.code)} onPick={() => pick(d.code)}
                label={districtPath(d, byCode, lang).join(' › ')} />
            ))
        ) : roots.length === 0
          ? <p className="px-3 py-3 text-sm text-muted-foreground">{L.empty}</p>
          : (
            <>
              {inTown.map(d => renderNode(d, 0))}
              {/* ⚠️ PERI_* is the SUBURB BELT — Mtskheta, Natakhtari, Tsavkisi,
                  Gardabani — sold as Tbilisi and reachable here on purpose (see
                  districtInCity). But sorted in by name it landed between Gldani
                  and Didube, so a list headed "Tbilisi" offered Mtskheta as one of
                  its districts (owner, 2026-08-08: "below we should see districts
                  in tbilisi"). Separated, not removed: these are real answers for a
                  property that really is out there, and dropping them would make
                  those owners unable to say where they live. */}
              {around.length > 0 && (
                <>
                  <p className="border-t border-border/60 px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{L.peri}</p>
                  {around.map(d => renderNode(d, 0))}
                </>
              )}
            </>
          )}
      </div>

      <p className="text-xs leading-5 text-muted-foreground">{hierarchical ? L.hint : L.flat}</p>
    </div>
  )
}
